import React, { useState, useEffect, useMemo } from 'react';
import { logger } from '../utils/logger';
import { 
  searchFeatures, 
  getFeatureLinkCountsBatch, 
  getUserCompany, 
  getFeatureVoteCounts, 
  toggleFeatureVote, 
  updateFeature,
  deleteFeature,
  getDimensions,
  getTagsByDimension,
  getFeatureTags,
  setFeatureTags
} from '../services/featureService';
import { getReleases, setFeatureReleases } from '../services/releaseService';
import ExportFeatureDialog from './ExportFeatureDialog';
import { DIMENSION_TAG_LIST_CHANGED } from '../utils/dimensionTagSync';
import './FeatureTable.css';

// Constants moved outside component to avoid recreation on every render
const STATUS_OPTIONS = ['Live', 'Testing', 'In development', 'Planning', 'Requested', 'Won\'t do', 'No Status'];

const FeatureTable = ({ features, filter, onFilterChange, onEdit, onDelete, onAddSubFeature, onMove, onAddFeature, onViewFeature, expandedNodes = new Set(), onExpandedNodesChange, featureGroupId = null, featureReleases = new Map(), mode = 'edit', onModeChange, userId = null, userRole = null, onReloadFeatures = null }) => {
  const [filteredFeatures, setFilteredFeatures] = useState(features);
  const [, setLinkCounts] = useState(new Map());
  const [voteCounts, setVoteCounts] = useState(new Map());
  const [userCompany, setUserCompany] = useState(null);
  const [companyVotes, setCompanyVotes] = useState(new Map()); // featureId -> boolean (has company voted)
  const [votesLoading, setVotesLoading] = useState(false);
  const [hoveredVoteFeature, setHoveredVoteFeature] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const [openReleaseMenuId, setOpenReleaseMenuId] = useState(null);
  const [exportDialogFeature, setExportDialogFeature] = useState(null);
  const [showViewsManagementDialog, setShowViewsManagementDialog] = useState(false);
  const [editingView, setEditingView] = useState(null); // null for new, or view object for edit
  const [viewName, setViewName] = useState('');
  const [views, setViews] = useState([]); // Array of {id, name, columns: [col1, col2, col3]}
  const [currentViewId, setCurrentViewId] = useState(null); // ID of currently selected view
  const [openViewMenu, setOpenViewMenu] = useState(false);
  const [openTaggingValueMenuId, setOpenTaggingValueMenuId] = useState(null); // "featureId-colIndex" or null
  const [openParentTagSummaryId, setOpenParentTagSummaryId] = useState(null); // "featureId-colIndex" or null
  const [deleteDialogFeature, setDeleteDialogFeature] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });
  const [sortingFeatureId, setSortingFeatureId] = useState(null); // Feature ID being sorted, or null if not in sort mode
  
  // Filter state - memoize storage key to avoid unnecessary effect runs
  const FILTER_STORAGE_KEY = useMemo(() => `feature-filter-${featureGroupId || 'default'}`, [featureGroupId]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Initialize filters from localStorage using lazy initialization
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    if (mode === 'edit' && featureGroupId) {
      try {
        const key = `feature-filter-${featureGroupId || 'default'}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.statuses && Array.isArray(parsed.statuses)) {
            return new Set(parsed.statuses);
          }
        }
      } catch (err) {
        logger.error('Error loading initial filter state:', err);
      }
    }
    return new Set();
  });
  
  const [selectedReleases, setSelectedReleases] = useState(() => {
    if (mode === 'edit' && featureGroupId) {
      try {
        const key = `feature-filter-${featureGroupId || 'default'}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.releases && Array.isArray(parsed.releases)) {
            return new Set(parsed.releases);
          }
        }
      } catch (err) {
        logger.error('Error loading initial filter state:', err);
      }
    }
    return new Set();
  });
  
  // Tagging View state
  const TAGGING_VIEWS_KEY = `tagging-views-${featureGroupId || 'default'}`;
  const TAGGING_CURRENT_VIEW_KEY = `tagging-current-view-${featureGroupId || 'default'}`;
  const [taggingColumns, setTaggingColumns] = useState([null, null, null]); // [col1, col2, col3] - can be 'release' or dimensionId
  const [dimensions, setDimensions] = useState([]);
  const [releases, setReleases] = useState([]);
  const [dimensionTagsMap, setDimensionTagsMap] = useState(new Map()); // Map<dimensionId, tags[]> - all available tags per dimension
  const [dimensionTagCatalogNonce, setDimensionTagCatalogNonce] = useState(0);
  const [featureTagsMap, setFeatureTagsMap] = useState(new Map()); // Map<featureId, Map<dimensionId, tags[]>>
  const [taggingFilters, setTaggingFilters] = useState(new Map()); // Map<columnKey, Set<tagIds>>
  const [openFilterPanel, setOpenFilterPanel] = useState(null); // null or {colIndex, colValue}
  const [filterPanelSearch, setFilterPanelSearch] = useState(''); // Search term for filter panel

  // Load filter state from localStorage when switching feature groups or modes
  // This ensures filters are restored even if the component remounts
  useEffect(() => {
    if (mode === 'edit' && featureGroupId) {
      try {
        const saved = localStorage.getItem(FILTER_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.statuses && Array.isArray(parsed.statuses) && parsed.statuses.length > 0) {
            setSelectedStatuses(new Set(parsed.statuses));
          }
          if (parsed.releases && Array.isArray(parsed.releases) && parsed.releases.length > 0) {
            setSelectedReleases(new Set(parsed.releases));
          }
        }
        // Don't clear if no saved state - keep current filters when switching back to edit mode
      } catch (err) {
        logger.error('Error loading filter state:', err);
      }
    }
    // Don't clear filters when switching away from edit mode - they'll be restored when switching back
  }, [featureGroupId, mode, FILTER_STORAGE_KEY]);

  // Load views and current view from localStorage
  useEffect(() => {
    if (mode === 'tagging' && featureGroupId) {
      try {
        // Load views list
        const savedViews = localStorage.getItem(TAGGING_VIEWS_KEY);
        if (savedViews) {
          const parsedViews = JSON.parse(savedViews);
          setViews(Array.isArray(parsedViews) ? parsedViews : []);
        } else {
          setViews([]);
        }

        // Load current view ID
        const savedCurrentViewId = localStorage.getItem(TAGGING_CURRENT_VIEW_KEY);
        if (savedCurrentViewId) {
          setCurrentViewId(savedCurrentViewId);
        } else {
          setCurrentViewId(null);
        }
      } catch (err) {
        logger.error('Error loading views:', err);
        setViews([]);
        setCurrentViewId(null);
      }
    }
  }, [featureGroupId, mode, TAGGING_VIEWS_KEY, TAGGING_CURRENT_VIEW_KEY]);

  // Load columns from current view
  useEffect(() => {
    if (mode === 'tagging' && featureGroupId) {
      if (currentViewId && views.length > 0) {
        const currentView = views.find(v => v.id === currentViewId);
        if (currentView && currentView.columns) {
          // Only update if columns are different to avoid infinite loop
          const columnsEqual = JSON.stringify(currentView.columns) === JSON.stringify(taggingColumns);
          if (!columnsEqual) {
            setTaggingColumns(currentView.columns);
          }
        } else {
          // View ID exists but view not found - clear it and use empty columns
          setCurrentViewId(null);
          setTaggingColumns([null, null, null]);
          try {
            localStorage.removeItem(TAGGING_CURRENT_VIEW_KEY);
          } catch (err) {
            logger.error('Error clearing current view:', err);
          }
        }
      } else {
        // No view selected or no views exist - use empty columns
        const emptyColumns = [null, null, null];
        const columnsEqual = JSON.stringify(emptyColumns) === JSON.stringify(taggingColumns);
        if (!columnsEqual) {
          setTaggingColumns(emptyColumns);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, featureGroupId, currentViewId, views, TAGGING_CURRENT_VIEW_KEY]);

  // Save current view columns when they change (only if a view is selected)
  useEffect(() => {
    if (mode === 'tagging' && featureGroupId && currentViewId && views.length > 0) {
      const currentView = views.find(v => v.id === currentViewId);
      if (currentView) {
        // Only update if columns actually changed to avoid infinite loop
        const columnsEqual = JSON.stringify(currentView.columns) === JSON.stringify(taggingColumns);
        if (!columnsEqual) {
          // Update the view in the views array
          setViews(prevViews => {
            const updatedViews = prevViews.map(v => 
              v.id === currentViewId 
                ? { ...v, columns: taggingColumns }
                : v
            );
            try {
              localStorage.setItem(TAGGING_VIEWS_KEY, JSON.stringify(updatedViews));
            } catch (err) {
              logger.error('Error saving views:', err);
            }
            return updatedViews;
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taggingColumns, currentViewId, featureGroupId, mode, TAGGING_VIEWS_KEY]);

  // Load dimensions and releases for tagging view and edit mode (for release dropdown)
  useEffect(() => {
    if ((mode === 'tagging' || mode === 'edit') && featureGroupId) {
      const loadData = async () => {
        try {
          const [dims, rels] = await Promise.all([
            mode === 'tagging' ? getDimensions(featureGroupId) : Promise.resolve([]),
            getReleases(featureGroupId)
          ]);
          
          // Filter dimensions by visibility (only for tagging view)
          if (mode === 'tagging') {
            const filteredDims = dims.filter(dim => {
              if (userRole === 'Guest') return dim.visible_to_guest;
              if (userRole === 'Editor') return dim.visible_to_editor;
              if (userRole === 'Owner') return dim.visible_to_owner;
              return false;
            });
            setDimensions(filteredDims);
            
            // Load all tags for each dimension (only in tagging mode)
            const tagsMap = new Map();
            for (const dim of filteredDims) {
              try {
                const tags = await getTagsByDimension(dim.id);
                tagsMap.set(dim.id, tags);
              } catch (err) {
                logger.error(`Error loading tags for dimension ${dim.id}:`, err);
              }
            }
            setDimensionTagsMap(tagsMap);
          }
          
          setReleases(rels || []);
        } catch (err) {
          logger.error('Error loading dimensions/releases:', err);
        }
      };
      loadData();
    }
  }, [mode, featureGroupId, userRole, dimensionTagCatalogNonce]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.featureGroupId === featureGroupId) {
        setDimensionTagCatalogNonce((n) => n + 1);
      }
    };
    window.addEventListener(DIMENSION_TAG_LIST_CHANGED, handler);
    return () => window.removeEventListener(DIMENSION_TAG_LIST_CHANGED, handler);
  }, [featureGroupId]);

  // Save filter state to localStorage whenever filters change
  useEffect(() => {
    if (mode === 'edit' && featureGroupId) {
      try {
        const filterState = {
          statuses: Array.from(selectedStatuses),
          releases: Array.from(selectedReleases)
        };
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterState));
      } catch (err) {
        logger.error('Error saving filter state:', err);
      }
    }
  }, [selectedStatuses, selectedReleases, featureGroupId, mode, FILTER_STORAGE_KEY]);

  // Collect all feature IDs from the features tree
  const getAllFeatureIds = React.useCallback((featuresList) => {
    const ids = new Set();
    const collectIds = (items) => {
      items.forEach(item => {
        ids.add(item.id);
        if (item.subFeatures && item.subFeatures.length > 0) {
          collectIds(item.subFeatures);
        }
      });
    };
    collectIds(featuresList);
    return ids;
  }, []);

  // Find a feature by ID in the features tree
  const findFeatureById = React.useCallback((featuresList, featureId) => {
    for (const feature of featuresList) {
      if (feature.id === featureId) {
        return feature;
      }
      if (feature.subFeatures && feature.subFeatures.length > 0) {
        const found = findFeatureById(feature.subFeatures, featureId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Load feature tags for all features in tagging view
  useEffect(() => {
    if (mode === 'tagging' && filteredFeatures.length > 0) {
      const loadFeatureTags = async () => {
        try {
          const tagsMap = new Map();
          for (const feature of filteredFeatures) {
            const allFeatureIds = getAllFeatureIds([feature]);
            for (const featureId of allFeatureIds) {
              try {
                const tags = await getFeatureTags(featureId);
                // Group by dimension
                const byDimension = new Map();
                for (const tag of tags) {
                  if (tag.dimension?.id) {
                    const dimId = tag.dimension.id;
                    if (!byDimension.has(dimId)) {
                      byDimension.set(dimId, []);
                    }
                    byDimension.get(dimId).push(tag);
                  }
                }
                tagsMap.set(featureId, byDimension);
              } catch (err) {
                logger.error(`Error loading tags for feature ${featureId}:`, err);
              }
            }
          }
          setFeatureTagsMap(tagsMap);
        } catch (err) {
          logger.error('Error loading feature tags:', err);
        }
      };
      loadFeatureTags();
    }
  }, [mode, filteredFeatures, getAllFeatureIds]);

  // Filter function that keeps parent nodes of matching children
  // Memoized with stable dependencies to prevent unnecessary recalculations
  const filterFeaturesWithParents = React.useCallback((featuresList, statusFilter, releaseFilter) => {
    if (statusFilter.size === 0 && releaseFilter.size === 0) {
      return featuresList;
    }

    const hasMatch = (feature) => {
      // Check status match (if status filter is active)
      const statusMatch = statusFilter.size === 0 || statusFilter.has(feature.status || 'No Status');
      
      // Check release match (if release filter is active)
      const featureReleasesList = featureReleases.get(feature.id) || [];
      const releaseMatch = releaseFilter.size === 0 || 
        featureReleasesList.some(release => releaseFilter.has(release.id));
      
      // Feature matches if it matches ALL active filters (AND logic)
      // If a filter is empty (size === 0), it's considered a match (no filter applied)
      return statusMatch && releaseMatch;
    };

    const hasMatchingDescendant = (feature) => {
      // Check if this feature matches
      if (hasMatch(feature)) return true;
      
      // Check if any descendant matches
      if (feature.subFeatures && feature.subFeatures.length > 0) {
        return feature.subFeatures.some(sub => hasMatchingDescendant(sub));
      }
      return false;
    };

    const filterRecursive = (items) => {
      return items
        .map(item => {
          const shouldInclude = hasMatchingDescendant(item);
          if (!shouldInclude) return null;
          
          const filtered = { ...item };
          if (item.subFeatures && item.subFeatures.length > 0) {
            filtered.subFeatures = filterRecursive(item.subFeatures);
          }
          return filtered;
        })
        .filter(Boolean);
    };

    return filterRecursive(featuresList);
  }, [featureReleases]);

  // Update filtered features when filter, features, or filter selections change
  // Debounce text filter to reduce API calls during typing
  useEffect(() => {
    const applyFilter = async () => {
      let results = features;
      
      // Apply text filter first (debounced)
      if (filter.trim()) {
        try {
          results = await searchFeatures(filter, featureGroupId);
        } catch (error) {
          logger.error('Error filtering features:', error);
          results = features;
        }
      }
      
      // Apply status and release filters (only in Status View mode)
      if (mode === 'edit' && (selectedStatuses.size > 0 || selectedReleases.size > 0)) {
        results = filterFeaturesWithParents(results, selectedStatuses, selectedReleases);
      }
      
      // Apply tagging view filters
      if (mode === 'tagging' && taggingFilters.size > 0) {
        const filterFeaturesByTags = (featuresList) => {
          return featuresList.filter(feature => {
            // Check if feature matches all column filters (AND logic)
            for (let colIndex = 0; colIndex < taggingColumns.length; colIndex++) {
              const col = taggingColumns[colIndex];
              if (!col) continue;
              
              const filterKey = `col${colIndex}`;
              const columnFilter = taggingFilters.get(filterKey);
              if (!columnFilter || columnFilter.size === 0) continue;
              
              if (col === 'release') {
                // Filter by release
                const featureReleasesList = featureReleases.get(feature.id) || [];
                const hasMatchingRelease = featureReleasesList.some(r => columnFilter.has(r.id));
                if (!hasMatchingRelease) {
                  return false;
                }
              } else {
                // Filter by dimension tags
                const featureTags = featureTagsMap.get(feature.id);
                const dimensionTags = featureTags?.get(col) || [];
                const hasMatchingTag = dimensionTags.some(tag => columnFilter.has(tag.id));
                if (!hasMatchingTag) {
                  return false;
                }
              }
            }
            
            return true;
          }).map(feature => {
            if (feature.subFeatures && feature.subFeatures.length > 0) {
              return {
                ...feature,
                subFeatures: filterFeaturesByTags(feature.subFeatures)
              };
            }
            return feature;
          });
        };
        
        results = filterFeaturesByTags(results);
      }
      
      setFilteredFeatures(results);
    };

    // Debounce text filter searches (300ms delay)
    const debounceTimer = setTimeout(() => {
      applyFilter();
    }, filter.trim() ? 300 : 0); // No delay if filter is empty

    return () => clearTimeout(debounceTimer);
  }, [filter, features, selectedStatuses, selectedReleases, mode, featureGroupId, filterFeaturesWithParents, taggingFilters, taggingColumns, featureTagsMap, featureReleases]);

  // Clear sort mode if the feature being sorted is no longer available
  useEffect(() => {
    if (sortingFeatureId) {
      const allIds = getAllFeatureIds(filteredFeatures);
      if (!allIds.has(sortingFeatureId)) {
        setSortingFeatureId(null);
      }
    }
  }, [filteredFeatures, sortingFeatureId, getAllFeatureIds]);

  // Load link counts for all features
  useEffect(() => {
    const loadLinkCounts = async () => {
      const allIds = getAllFeatureIds(filteredFeatures);
      if (allIds.size > 0) {
        try {
          const counts = await getFeatureLinkCountsBatch(Array.from(allIds));
          setLinkCounts(counts);
        } catch (error) {
          logger.error('Error loading link counts:', error);
        }
      } else {
        setLinkCounts(new Map());
      }
    };

    loadLinkCounts();
  }, [filteredFeatures, getAllFeatureIds]);

  // Load user company
  useEffect(() => {
    const loadUserCompany = async () => {
      if (userId && featureGroupId && (mode === 'userVote' || mode === 'adminVote')) {
        try {
          const company = await getUserCompany(userId, featureGroupId);
          setUserCompany(company);
        } catch (error) {
          logger.error('Error loading user company:', error);
        }
      }
    };

    loadUserCompany();
  }, [userId, featureGroupId, mode]);

  // Load vote counts for all features
  useEffect(() => {
    const loadVoteCounts = async () => {
      if (featureGroupId && (mode === 'userVote' || mode === 'adminVote')) {
        const allIds = getAllFeatureIds(filteredFeatures);
        if (allIds.size > 0) {
          // Set loading state immediately when we start loading
          setVotesLoading(true);
          try {
            const counts = await getFeatureVoteCounts(Array.from(allIds), featureGroupId);
            setVoteCounts(counts);

            // Extract company votes from the already-loaded vote counts (no additional API calls needed)
            // Note: userCompany might still be loading, so we check it here
            if (userCompany?.company_id && mode === 'userVote') {
              const companyVotesMap = new Map();
              for (const featureId of allIds) {
                const voteData = counts.get(featureId);
                if (voteData && voteData.companyVotes) {
                  const companyVote = voteData.companyVotes.find(cv => cv.company_id === userCompany.company_id);
                  companyVotesMap.set(featureId, companyVote?.voted || false);
                } else {
                  companyVotesMap.set(featureId, false);
                }
              }
              setCompanyVotes(companyVotesMap);
            }
          } catch (error) {
            logger.error('Error loading vote counts:', error);
          } finally {
            setVotesLoading(false);
          }
        } else {
          setVoteCounts(new Map());
          setCompanyVotes(new Map());
          setVotesLoading(false);
        }
      } else {
        // Clear vote data when not in vote mode
        setVoteCounts(new Map());
        setCompanyVotes(new Map());
        setVotesLoading(false);
      }
    };

    // For userVote mode, wait for userCompany to load before starting
    // But show loading state immediately
    if (mode === 'userVote' && !userCompany && userId && featureGroupId) {
      setVotesLoading(true);
      return;
    }

    loadVoteCounts();
  }, [filteredFeatures, featureGroupId, mode, userCompany, getAllFeatureIds, userId]);

  // Clean up expanded rows when features change (remove IDs that no longer exist)
  // This runs automatically when features change, but we don't want to interfere
  // with the parent's restoration logic, so we'll let the parent handle it

  const toggleRow = React.useCallback((id) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    if (onExpandedNodesChange) {
      onExpandedNodesChange(newExpanded);
    }
  }, [expandedNodes, onExpandedNodesChange]);

  const handleVoteToggle = async (featureId, currentVote) => {
    if (!userCompany?.company_id || !featureGroupId) {
      alert('You must be assigned to a company to vote');
      return;
    }

    // Check if feature status allows voting
    const feature = findFeatureById(features, featureId);
    if (feature) {
      // Check if it's a parent feature
      if (feature.subFeatures && feature.subFeatures.length > 0) {
        alert("You can't vote on folders, please choose a sub-feature");
        return;
      }
      
      // Check if status is restricted
      const restrictedStatuses = ['Live', 'Testing', 'Won\'t do'];
      if (restrictedStatuses.includes(feature.status || 'No Status')) {
        alert(`Cannot vote on features with status: ${feature.status}`);
        return;
      }
    }

    try {
      await toggleFeatureVote(featureId, userCompany.company_id, featureGroupId, currentVote);
      
      // Update local state
      const newCompanyVotes = new Map(companyVotes);
      newCompanyVotes.set(featureId, !currentVote);
      setCompanyVotes(newCompanyVotes);

      // Reload vote counts
      const allIds = getAllFeatureIds(filteredFeatures);
      if (allIds.size > 0) {
        const counts = await getFeatureVoteCounts(Array.from(allIds), featureGroupId);
        setVoteCounts(counts);
      }
    } catch (error) {
      logger.error('Error toggling vote:', error);
      alert('Failed to update vote');
    }
  };

  const getStatusClass = React.useCallback((status) => {
    const statusMap = {
      'Live': 'status-live',
      'Testing': 'status-testing',
      'In development': 'status-in-development',
      'Planning': 'status-planning',
      'Requested': 'status-requested',
      'Won\'t do': 'status-wont-do',
      'No Status': 'status-no-status'
    };
    return statusMap[status] || 'status-no-status';
  }, []);

  // Recursively count statuses of all children (excluding parent features - only count leaf features)
  const getChildStatusCounts = (feature) => {
    const counts = {
      'Live': 0,
      'Testing': 0,
      'In development': 0,
      'Planning': 0,
      'Requested': 0,
      'Won\'t do': 0,
      'No Status': 0
    };

    const countRecursive = (f) => {
      if (f.subFeatures && f.subFeatures.length > 0) {
        f.subFeatures.forEach(subFeature => {
          // Only count features that are leaf nodes (no sub-features)
          const isLeaf = !subFeature.subFeatures || subFeature.subFeatures.length === 0;
          if (isLeaf) {
            const status = subFeature.status || 'No Status';
            if (counts.hasOwnProperty(status)) {
              counts[status]++;
            }
          } else {
            // If this feature has sub-features, recursively count only its leaf children
            countRecursive(subFeature);
          }
        });
      }
    };

    countRecursive(feature);
    return counts;
  };

  const handleStatusClick = (e, featureId) => {
    e.stopPropagation();
    if (userRole === 'Owner') {
      setOpenStatusMenuId(openStatusMenuId === featureId ? null : featureId);
    }
  };

  const handleStatusChange = async (featureId, newStatus) => {
    try {
      // Optimistically update the feature in the local state
      const updateFeatureInTree = (featuresList) => {
        return featuresList.map(f => {
          if (f.id === featureId) {
            return { ...f, status: newStatus };
          }
          if (f.subFeatures && f.subFeatures.length > 0) {
            return { ...f, subFeatures: updateFeatureInTree(f.subFeatures) };
          }
          return f;
        });
      };

      setFilteredFeatures(prev => updateFeatureInTree(prev));

      // Update in database
      await updateFeature(featureId, { status: newStatus });
      
      setOpenStatusMenuId(null);
    } catch (error) {
      logger.error('Error updating feature status:', error);
      alert('Failed to update status');
      // Reload features on error
      if (onFilterChange) {
        // Trigger a reload by clearing and re-applying filter
        const currentFilter = filter;
        onFilterChange('');
        setTimeout(() => onFilterChange(currentFilter), 100);
      }
    }
  };

  const handleReleaseClick = (e, featureId) => {
    e.stopPropagation();
    if (userRole === 'Owner' || userRole === 'Editor') {
      setOpenReleaseMenuId(openReleaseMenuId === featureId ? null : featureId);
    }
  };

  const handleReleaseChange = async (featureId, releaseId, isAdding) => {
    try {
      const currentReleases = featureReleases.get(featureId) || [];
      let newReleaseIds;
      
      if (isAdding) {
        // Add release if not already present
        if (!currentReleases.find(r => r.id === releaseId)) {
          newReleaseIds = [...currentReleases.map(r => r.id), releaseId];
        } else {
          return; // Already has this release
        }
      } else {
        // Remove release
        newReleaseIds = currentReleases.filter(r => r.id !== releaseId).map(r => r.id);
      }

      // Update in database
      await setFeatureReleases(featureId, newReleaseIds);
      
      setOpenReleaseMenuId(null);
      
      // Reload features to get updated release data
      if (onReloadFeatures) {
        await onReloadFeatures();
      }
    } catch (error) {
      logger.error('Error updating feature releases:', error);
      alert('Failed to update releases');
      // Reload features on error
      if (onReloadFeatures) {
        await onReloadFeatures();
      }
    }
  };

  const handleTaggingValueClick = (e, featureId, colIndex) => {
    e.stopPropagation();
    const menuKey = `${featureId}-${colIndex}`;
    setOpenTaggingValueMenuId(openTaggingValueMenuId === menuKey ? null : menuKey);
  };

  const handleTaggingReleaseChange = async (featureId, releaseId, isAdding) => {
    try {
      const currentReleases = featureReleases.get(featureId) || [];
      let newReleaseIds;
      
      if (isAdding) {
        if (!currentReleases.find(r => r.id === releaseId)) {
          newReleaseIds = [...currentReleases.map(r => r.id), releaseId];
        } else {
          return;
        }
      } else {
        newReleaseIds = currentReleases.filter(r => r.id !== releaseId).map(r => r.id);
      }

      await setFeatureReleases(featureId, newReleaseIds);
      setOpenTaggingValueMenuId(null);
      
      if (onReloadFeatures) {
        await onReloadFeatures();
      }
    } catch (error) {
      logger.error('Error updating feature releases:', error);
      alert('Failed to update releases');
      if (onReloadFeatures) {
        await onReloadFeatures();
      }
    }
  };

  const handleTaggingTagChange = async (featureId, dimensionId, tagId, isAdding, dimensionType) => {
    try {
      const featureTags = featureTagsMap.get(featureId);
      const dimensionTags = featureTags?.get(dimensionId) || [];
      const currentTag = dimensionTags.find(t => t.id === tagId);
      
      if (isAdding) {
        // Add tag
        if (!currentTag) {
          let tagAssignments = [];
          
          // Get all current tags for this feature
          if (featureTags) {
            for (const [, tags] of featureTags.entries()) {
              for (const tag of tags) {
                tagAssignments.push({
                  tag_id: tag.id,
                  state: tag.state || null,
                  rating: tag.rating || null,
                  comment: tag.comment || null
                });
              }
            }
          }
          
          // Add new tag with default values
          let defaultState = null;
          let defaultRating = null;
          
          if (dimensionType === 'BOOLEAN') {
            defaultState = 'TRUE';
          } else if (dimensionType === 'TRI_STATE') {
            defaultState = 'UNSET';
          } else if (dimensionType === 'RATING') {
            defaultRating = null;
          }
          
          tagAssignments.push({
            tag_id: tagId,
            state: defaultState,
            rating: defaultRating,
            comment: null
          });
          
          await setFeatureTags(featureId, tagAssignments);
        }
      } else {
        // Remove tag
        if (currentTag) {
          const tagAssignments = [];
          
          // Get all current tags except the one being removed
          if (featureTags) {
            for (const [, tags] of featureTags.entries()) {
              for (const tag of tags) {
                if (tag.id !== tagId) {
                  tagAssignments.push({
                    tag_id: tag.id,
                    state: tag.state || null,
                    rating: tag.rating || null,
                    comment: tag.comment || null
                  });
                }
              }
            }
          }
          
          await setFeatureTags(featureId, tagAssignments);
        }
      }
      
      setOpenTaggingValueMenuId(null);
      
      if (onReloadFeatures) {
        await onReloadFeatures();
      }
    } catch (error) {
      logger.error('Error updating feature tags:', error);
      alert('Failed to update tags');
      if (onReloadFeatures) {
        await onReloadFeatures();
      }
    }
  };

  const handleMenuClick = React.useCallback((e, featureId) => {
    e.stopPropagation();
    // Don't open menu if in sort mode
    if (sortingFeatureId) return;
    setOpenMenuId(openMenuId === featureId ? null : featureId);
  }, [openMenuId, sortingFeatureId]);

  const handleStartSort = (featureId) => {
    setSortingFeatureId(featureId);
    setOpenMenuId(null); // Close any open menus
  };

  const handleCancelSort = () => {
    setSortingFeatureId(null);
  };

  const handleAfterThis = async (targetFeatureId) => {
    if (!sortingFeatureId || !onReloadFeatures) return;
    
    try {
      // Find the target feature in the features tree
      const findFeature = (featuresList, id) => {
        for (const f of featuresList) {
          if (f.id === id) return f;
          if (f.subFeatures && f.subFeatures.length > 0) {
            const found = findFeature(f.subFeatures, id);
            if (found) return found;
          }
        }
        return null;
      };

      const targetFeature = findFeature(filteredFeatures, targetFeatureId);
      if (!targetFeature) {
        logger.error('Target feature not found');
        return;
      }

      // Get the target feature's parent_id
      const newParentId = targetFeature.parent_id || null;
      const targetSortOrder = targetFeature.sort_order || 0;
      const newSortOrder = targetSortOrder + 1;

      // Update the feature being sorted
      await updateFeature(sortingFeatureId, {
        parent_id: newParentId,
        sort_order: newSortOrder
      });

      // Reload features to reflect changes
      await onReloadFeatures();
      
      // Stay in sort mode so user can continue sorting
    } catch (error) {
      logger.error('Error moving feature:', error);
      alert('Failed to move feature. Please try again.');
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.action-menu-container')) {
        setOpenMenuId(null);
      }
      if (!e.target.closest('.status-menu-container')) {
        setOpenStatusMenuId(null);
      }
      if (!e.target.closest('.release-menu-container')) {
        setOpenReleaseMenuId(null);
      }
      if (!e.target.closest('.tagging-value-menu-container')) {
        setOpenTaggingValueMenuId(null);
      }
      if (!e.target.closest('.tagging-parent-summary-container')) {
        setOpenParentTagSummaryId(null);
      }
      if (!e.target.closest('.tagging-view-selector-container')) {
        setOpenViewMenu(false);
      }
    };
    if (openMenuId || openStatusMenuId || openReleaseMenuId || openTaggingValueMenuId || openParentTagSummaryId || openViewMenu) {
      // Use setTimeout to ensure this runs after the click event that opened the menu
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId, openStatusMenuId, openReleaseMenuId, openTaggingValueMenuId, openParentTagSummaryId, openViewMenu]);

  // Calculate count of direct child features that the company has voted on
  // Only counts direct children, not grandchildren (to avoid double-counting)
  // The sum of all parent pills should equal "Votes Cast"
  const getChildVoteTotal = React.useCallback((feature) => {
    let count = 0;
    if (feature.subFeatures && feature.subFeatures.length > 0) {
      feature.subFeatures.forEach(subFeature => {
        // Only count direct children (leaf features that the company voted on)
        // Don't count grandchildren - they're counted by their own parent
        if (!subFeature.subFeatures || subFeature.subFeatures.length === 0) {
          // This is a leaf feature
          if (companyVotes.get(subFeature.id) === true) {
            count += 1;
          }
        } else {
          // This is a parent - recursively count its children
          count += getChildVoteTotal(subFeature);
        }
      });
    }
    return count;
  }, [companyVotes]);

  // Recursively count all children (including grandchildren, etc.)
  const countAllChildren = React.useCallback((feature) => {
    let count = 0;
    if (feature.subFeatures && feature.subFeatures.length > 0) {
      feature.subFeatures.forEach(subFeature => {
        count += 1; // Count this child
        count += countAllChildren(subFeature); // Recursively count its children
      });
    }
    return count;
  }, []);

  // Recursively collect all child feature IDs (including grandchildren, etc.)
  // Returns IDs in depth-first order (deepest children first) for safe deletion
  const collectAllChildIds = React.useCallback((feature) => {
    const ids = [];
    if (feature.subFeatures && feature.subFeatures.length > 0) {
      feature.subFeatures.forEach(subFeature => {
        // First collect all descendants (deepest first)
        ids.push(...collectAllChildIds(subFeature));
        // Then add this child (so deepest are deleted first)
        ids.push(subFeature.id);
      });
    }
    return ids;
  }, []);

  // Calculate tag counts for all children of a parent feature for a specific dimension
  const getChildTagCounts = React.useCallback((feature, dimensionId) => {
    const childIds = collectAllChildIds(feature);
    const tagCounts = new Map(); // Map<tagId, count>
    let totalChildren = 0;
    let taggedChildren = 0;

    childIds.forEach(childId => {
      totalChildren++;
      const childTags = featureTagsMap.get(childId);
      const dimensionTags = childTags?.get(dimensionId) || [];
      if (dimensionTags.length > 0) {
        taggedChildren++;
      }
      dimensionTags.forEach(tag => {
        tagCounts.set(tag.id, (tagCounts.get(tag.id) || 0) + 1);
      });
    });

    return {
      tagCounts: Array.from(tagCounts.entries()).map(([tagId, count]) => {
        // Find the tag object from dimensionTagsMap
        const allTags = dimensionTagsMap.get(dimensionId) || [];
        const tag = allTags.find(t => t.id === tagId);
        return { tag, count };
      }).filter(item => item.tag), // Filter out any tags that weren't found
      totalChildren,
      taggedChildren
    };
  }, [collectAllChildIds, featureTagsMap, dimensionTagsMap]);

  // Calculate release counts for all children of a parent feature
  const getChildReleaseCounts = React.useCallback((feature) => {
    const childIds = collectAllChildIds(feature);
    const releaseCounts = new Map(); // Map<releaseId, count>
    let totalChildren = 0;
    let taggedChildren = 0;

    childIds.forEach(childId => {
      totalChildren++;
      const childReleases = featureReleases.get(childId) || [];
      if (childReleases.length > 0) {
        taggedChildren++;
      }
      childReleases.forEach(release => {
        releaseCounts.set(release.id, (releaseCounts.get(release.id) || 0) + 1);
      });
    });

    return {
      releaseCounts: Array.from(releaseCounts.entries()).map(([releaseId, count]) => {
        const release = releases.find(r => r.id === releaseId);
        return { release, count };
      }).filter(item => item.release), // Filter out any releases that weren't found
      totalChildren,
      taggedChildren
    };
  }, [collectAllChildIds, featureReleases, releases]);

  // Memoize renderFeatureRow to prevent unnecessary re-renders
  const renderFeatureRow = React.useCallback((feature, level = 0, siblings = [], index = -1) => {
    const hasSubFeatures = feature.subFeatures && feature.subFeatures.length > 0;
    const isExpanded = expandedNodes.has(feature.id);
    const indent = level * 30;
    const canMoveUp = index > 0;
    const canMoveDown = index < siblings.length - 1;
    // counts variable removed - was unused
    const status = feature.status || 'No Status';
    const childStatusCounts = hasSubFeatures ? getChildStatusCounts(feature) : null;
    
    // Check if voting is allowed for this feature (not in Live, Testing, or Won't do, and not a parent)
    const restrictedStatuses = ['Live', 'Testing', 'Won\'t do'];
    const canVote = mode === 'userVote' ? (!restrictedStatuses.includes(status) && !hasSubFeatures) : true;
    
    // Calculate child vote total for parent features
    const childVoteTotal = hasSubFeatures ? getChildVoteTotal(feature) : 0;

    const isBeingSorted = sortingFeatureId === feature.id;
    
    return (
      <React.Fragment key={feature.id}>
        <tr className={`feature-row level-${level} ${isBeingSorted ? 'sorting-feature' : ''}`}>
          <td style={{ paddingLeft: `${indent + 10}px` }}>
            <div className="feature-name-cell">
              {hasSubFeatures ? (
                <button
                  className="expand-button"
                  onClick={() => toggleRow(feature.id)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                </button>
              ) : (
                <span className="expand-spacer" />
              )}
              <span 
                className="feature-name clickable" 
                title={`ID: ${feature.id}${feature.private_feature ? ' (Private)' : ''}`}
                onClick={() => onViewFeature && onViewFeature(feature)}
              >
                {feature.private_feature === true && (
                  <i className="fas fa-eye-slash private-feature-icon" title="Private Feature"></i>
                )}
                {feature.name}
              </span>
            </div>
          </td>
          <td className="description-cell">
            <div className="description-preview">
              {feature.description 
                ? (feature.description.length > 100 
                    ? feature.description.substring(0, 100) + '...' 
                    : feature.description)
                : 'No description'}
            </div>
          </td>
          {mode === 'edit' && (
            <td className="status-cell">
              {hasSubFeatures && childStatusCounts ? (
                <div className="child-status-indicators">
                  {STATUS_OPTIONS.map(statusOption => {
                    const count = childStatusCounts[statusOption];
                    if (count > 0) {
                      return (
                        <span
                          key={statusOption}
                          className={`child-status-dot ${getStatusClass(statusOption)}`}
                          title={`${count} child feature${count > 1 ? 's' : ''} with status: ${statusOption}`}
                        >
                          {count}
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                <div className="status-menu-container">
                  <span 
                    className={`status-pill ${getStatusClass(status)} ${userRole === 'Owner' ? 'clickable' : ''}`}
                    onClick={(e) => handleStatusClick(e, feature.id)}
                    title={userRole === 'Owner' ? 'Click to change status' : ''}
                  >
                    {status}
                    {userRole === 'Owner' && (
                      <i className="fas fa-chevron-down status-dropdown-icon"></i>
                    )}
                  </span>
                  {userRole === 'Owner' && openStatusMenuId === feature.id && (
                    <div className="status-menu-dropdown">
                      {STATUS_OPTIONS.map(option => (
                        <button
                          key={option}
                          className={`status-menu-item ${option === status ? 'active' : ''}`}
                          onClick={() => handleStatusChange(feature.id, option)}
                        >
                          <span className={`status-pill-small ${getStatusClass(option)}`}>
                            {option}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </td>
          )}
          {mode === 'edit' && (
            <td className="release-cell">
              {(userRole === 'Owner' || userRole === 'Editor') ? (
                <div className="release-menu-container">
                  <div 
                    className="release-badges clickable"
                    onClick={(e) => handleReleaseClick(e, feature.id)}
                    title="Click to change releases"
                  >
                    {(featureReleases.get(feature.id) || []).map(release => (
                      <span key={release.id} className="release-badge" title={`${release.name} (${release.status})`}>
                        {release.name}
                      </span>
                    ))}
                    {(!featureReleases.get(feature.id) || featureReleases.get(feature.id).length === 0) && (
                      <span className="no-release">—</span>
                    )}
                    <i className="fas fa-chevron-down release-dropdown-icon"></i>
                  </div>
                  {openReleaseMenuId === feature.id && (
                    <div className="release-menu-dropdown">
                      {releases.length === 0 ? (
                        <div className="release-menu-item" style={{ color: '#999', fontStyle: 'italic' }}>
                          No releases available
                        </div>
                      ) : (
                        releases.map(release => {
                          const isAssigned = (featureReleases.get(feature.id) || []).some(r => r.id === release.id);
                          return (
                            <button
                              key={release.id}
                              className={`release-menu-item ${isAssigned ? 'active' : ''}`}
                              onClick={() => handleReleaseChange(feature.id, release.id, !isAssigned)}
                            >
                              <span className="release-menu-checkbox">
                                {isAssigned && <i className="fas fa-check"></i>}
                              </span>
                              <span className="release-menu-name">{release.name}</span>
                              <span className="release-menu-status">({release.status})</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="release-badges">
                  {(featureReleases.get(feature.id) || []).map(release => (
                    <span key={release.id} className="release-badge" title={`${release.name} (${release.status})`}>
                      {release.name}
                    </span>
                  ))}
                  {(!featureReleases.get(feature.id) || featureReleases.get(feature.id).length === 0) && (
                    <span className="no-release">—</span>
                  )}
                </div>
              )}
            </td>
          )}
          {mode === 'tagging' && taggingColumns.map((col, colIndex) => {
            if (!col) return <td key={colIndex}></td>;
            
            const menuKey = `${feature.id}-${colIndex}`;
            const isMenuOpen = openTaggingValueMenuId === menuKey;
            const canEdit = userRole === 'Owner' || userRole === 'Editor';
            // Check if this is the rightmost non-empty column
            const isRightmostColumn = (() => {
              for (let i = colIndex + 1; i < taggingColumns.length; i++) {
                if (taggingColumns[i]) return false;
              }
              return true;
            })();
            
            if (col === 'release') {
              const featureReleasesList = featureReleases.get(feature.id) || [];
              
              // Check if this is a parent feature - show summary badge instead
              const parentSummaryKey = `${feature.id}-${colIndex}`;
              const isParentSummaryOpen = openParentTagSummaryId === parentSummaryKey;
              
              if (hasSubFeatures) {
                const childReleaseCounts = getChildReleaseCounts(feature);
                const { releaseCounts, totalChildren, taggedChildren } = childReleaseCounts;
                
                return (
                  <td key={colIndex} className="tagging-column-cell">
                    <div className="tagging-parent-summary-container">
                      <span 
                        className={`tagging-parent-summary-badge clickable ${isParentSummaryOpen ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenParentTagSummaryId(isParentSummaryOpen ? null : parentSummaryKey);
                          setOpenTaggingValueMenuId(null); // Close other menus
                        }}
                        title="Click to see release breakdown"
                      >
                        ({taggedChildren} of {totalChildren})
                      </span>
                      {isParentSummaryOpen && (
                        <div className={`tagging-parent-summary-dropdown ${isRightmostColumn ? 'right-aligned' : ''}`}>
                          <div className="tagging-parent-summary-header">
                            Release Breakdown
                          </div>
                          {releaseCounts.length === 0 ? (
                            <div className="tagging-parent-summary-item" style={{ color: '#999', fontStyle: 'italic' }}>
                              No releases assigned to children
                            </div>
                          ) : (
                            releaseCounts
                              .sort((a, b) => (a.release?.name || '').localeCompare(b.release?.name || ''))
                              .map(({ release, count }) => (
                                <div key={release.id} className="tagging-parent-summary-item">
                                  <span className="tagging-parent-summary-tag-name">{release.name}</span>
                                  <span className="tagging-parent-summary-count">{count}</span>
                                </div>
                              ))
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                );
              }
              
              return (
                <td key={colIndex} className="tagging-column-cell">
                  {canEdit ? (
                    <div className="tagging-value-menu-container">
                      {featureReleasesList.length > 0 ? (
                        <div 
                          className={`tagging-value-display clickable ${isMenuOpen ? 'active' : ''}`}
                          onClick={(e) => handleTaggingValueClick(e, feature.id, colIndex)}
                          title="Click to change releases"
                        >
                          <div className="tagging-pills-with-dropdown">
                            {featureReleasesList.map((release, idx) => (
                              <span 
                                key={release.id} 
                                className={`tagging-pill release-pill ${idx === featureReleasesList.length - 1 ? 'has-dropdown' : ''}`} 
                                title={release.name}
                              >
                                {release.name}
                                {idx === featureReleasesList.length - 1 && (
                                  <i className="fas fa-chevron-down tagging-value-dropdown-icon"></i>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <span 
                          className={`tagging-empty-badge clickable ${isMenuOpen ? 'active' : ''}`}
                          onClick={(e) => handleTaggingValueClick(e, feature.id, colIndex)}
                          title="Click to change releases"
                        >
                          —
                          <i className="fas fa-chevron-down tagging-value-dropdown-icon"></i>
                        </span>
                      )}
                      {isMenuOpen && (
                        <div className={`tagging-value-menu-dropdown ${isRightmostColumn ? 'right-aligned' : ''}`}>
                          {releases.length === 0 ? (
                            <div className="tagging-value-menu-item" style={{ color: '#999', fontStyle: 'italic' }}>
                              No releases available
                            </div>
                          ) : (
                            releases.map(release => {
                              const isAssigned = featureReleasesList.some(r => r.id === release.id);
                              return (
                                <button
                                  key={release.id}
                                  className={`tagging-value-menu-item ${isAssigned ? 'active' : ''}`}
                                  onClick={() => handleTaggingReleaseChange(feature.id, release.id, !isAssigned)}
                                >
                                  <span className="tagging-value-menu-checkbox">
                                    {isAssigned && <i className="fas fa-check"></i>}
                                  </span>
                                  <span className="tagging-value-menu-name">{release.name}</span>
                                  <span className="tagging-value-menu-status">({release.status})</span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="tagging-pills">
                      {featureReleasesList.map(release => (
                        <span key={release.id} className="tagging-pill release-pill" title={release.name}>
                          {release.name}
                        </span>
                      ))}
                      {featureReleasesList.length === 0 && <span className="no-tag">—</span>}
                    </div>
                  )}
                </td>
              );
            }
            
            // Dimension column
            const dimension = dimensions.find(d => d.id === col);
            const featureTags = featureTagsMap.get(feature.id);
            const dimensionTags = featureTags?.get(col) || [];
            
            // Sort tags alphabetically
            const sortedTags = [...dimensionTags].sort((a, b) => 
              (a.name || '').localeCompare(b.name || '')
            );
            
            const allTags = dimensionTagsMap.get(col) || [];
            const sortedAllTags = [...allTags].sort((a, b) => 
              (a.name || '').localeCompare(b.name || '')
            );

            // Check if this is a parent feature - show summary badge instead
            const parentSummaryKey = `${feature.id}-${colIndex}`;
            const isParentSummaryOpen = openParentTagSummaryId === parentSummaryKey;
            
            if (hasSubFeatures) {
              const childTagCounts = getChildTagCounts(feature, col);
              const { tagCounts, totalChildren, taggedChildren } = childTagCounts;
              
              return (
                <td key={colIndex} className="tagging-column-cell">
                  <div className="tagging-parent-summary-container">
                    <span 
                      className={`tagging-parent-summary-badge clickable ${isParentSummaryOpen ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenParentTagSummaryId(isParentSummaryOpen ? null : parentSummaryKey);
                        setOpenTaggingValueMenuId(null); // Close other menus
                      }}
                      title="Click to see tag breakdown"
                    >
                      ({taggedChildren} of {totalChildren})
                    </span>
                    {isParentSummaryOpen && (
                      <div className={`tagging-parent-summary-dropdown ${isRightmostColumn ? 'right-aligned' : ''}`}>
                        <div className="tagging-parent-summary-header">
                          Tag Breakdown
                        </div>
                        {tagCounts.length === 0 ? (
                          <div className="tagging-parent-summary-item" style={{ color: '#999', fontStyle: 'italic' }}>
                            No tags assigned to children
                          </div>
                        ) : (
                          tagCounts
                            .sort((a, b) => (a.tag?.name || '').localeCompare(b.tag?.name || ''))
                            .map(({ tag, count }) => (
                              <div key={tag.id} className="tagging-parent-summary-item">
                                <span className="tagging-parent-summary-tag-name">{tag.name}</span>
                                <span className="tagging-parent-summary-count">{count}</span>
                              </div>
                            ))
                        )}
                      </div>
                    )}
                  </div>
                </td>
              );
            }
            
            return (
              <td key={colIndex} className="tagging-column-cell">
                {canEdit ? (
                  <div className="tagging-value-menu-container">
                      {sortedTags.length > 0 ? (
                      <div 
                        className={`tagging-value-display clickable ${isMenuOpen ? 'active' : ''}`}
                        onClick={(e) => handleTaggingValueClick(e, feature.id, colIndex)}
                        title="Click to change tags"
                      >
                        <div className="tagging-pills-with-dropdown">
                          {sortedTags.map((tag, idx) => {
                            const pillKey = `${feature.id}-${tag.id}`;
                            const isLast = idx === sortedTags.length - 1;
                            return (
                              <span 
                                key={pillKey} 
                                className={`tagging-pill dimension-pill ${dimension?.dimension_type?.toLowerCase() || ''} ${isLast ? 'has-dropdown' : ''}`}
                                title={tag.comment || tag.name}
                              >
                                {tag.name}
                                {dimension?.dimension_type === 'TRI_STATE' && tag.state && (
                                  <span className={`tag-state ${tag.state.toLowerCase()}`}>
                                    {tag.state === 'YES' ? '✓' : tag.state === 'NO' ? '✗' : '—'}
                                  </span>
                                )}
                                {dimension?.dimension_type === 'RATING' && tag.rating && (
                                  <span className="tag-rating">⭐{tag.rating}</span>
                                )}
                                {tag.comment && (
                                  <span className="tag-comment-icon" title={tag.comment}>
                                    <i className="fas fa-comment"></i>
                                  </span>
                                )}
                                {isLast && (
                                  <i className="fas fa-chevron-down tagging-value-dropdown-icon"></i>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <span 
                        className={`tagging-empty-badge clickable ${isMenuOpen ? 'active' : ''}`}
                        onClick={(e) => handleTaggingValueClick(e, feature.id, colIndex)}
                        title="Click to change tags"
                      >
                        —
                        <i className="fas fa-chevron-down tagging-value-dropdown-icon"></i>
                      </span>
                    )}
                    {isMenuOpen && (
                      <div className={`tagging-value-menu-dropdown ${isRightmostColumn ? 'right-aligned' : ''}`}>
                        {sortedAllTags.length === 0 ? (
                          <div className="tagging-value-menu-item" style={{ color: '#999', fontStyle: 'italic' }}>
                            No tags available
                          </div>
                        ) : (
                          sortedAllTags.map(tag => {
                            const isAssigned = sortedTags.some(t => t.id === tag.id);
                            return (
                              <button
                                key={tag.id}
                                className={`tagging-value-menu-item ${isAssigned ? 'active' : ''}`}
                                onClick={() => handleTaggingTagChange(feature.id, col, tag.id, !isAssigned, dimension?.dimension_type)}
                              >
                                <span className="tagging-value-menu-checkbox">
                                  {isAssigned && <i className="fas fa-check"></i>}
                                </span>
                                <span className="tagging-value-menu-name">{tag.name}</span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="tagging-pills">
                    {sortedTags.map(tag => {
                      const pillKey = `${feature.id}-${tag.id}`;
                      return (
                        <span 
                          key={pillKey} 
                          className={`tagging-pill dimension-pill ${dimension?.dimension_type?.toLowerCase() || ''}`}
                          title={tag.comment || tag.name}
                        >
                          {tag.name}
                          {dimension?.dimension_type === 'TRI_STATE' && tag.state && (
                            <span className={`tag-state ${tag.state.toLowerCase()}`}>
                              {tag.state === 'YES' ? '✓' : tag.state === 'NO' ? '✗' : '—'}
                            </span>
                          )}
                          {dimension?.dimension_type === 'RATING' && tag.rating && (
                            <span className="tag-rating">⭐{tag.rating}</span>
                          )}
                          {tag.comment && (
                            <span className="tag-comment-icon" title={tag.comment}>
                              <i className="fas fa-comment"></i>
                            </span>
                          )}
                        </span>
                      );
                    })}
                    {sortedTags.length === 0 && <span className="no-tag">—</span>}
                  </div>
                )}
              </td>
            );
          })}
          {mode === 'edit' && userRole !== 'Guest' && (
            <td className="actions-cell">
              <div className="action-menu-container">
                {sortingFeatureId ? (
                  // Sort mode: show "After this" or "Cancel Sort"
                  feature.id === sortingFeatureId ? (
                    <button
                      className="action-button-sort-cancel"
                      onClick={handleCancelSort}
                      title="Cancel Sort"
                    >
                      Cancel Sort
                    </button>
                  ) : feature.id !== sortingFeatureId ? (
                    <button
                      className="action-button-sort-after"
                      onClick={() => handleAfterThis(feature.id)}
                      title="Move sorting feature to be a sibling after this feature"
                    >
                      After this
                    </button>
                  ) : null
                ) : (
                  // Normal mode: show action menu
                  <>
                    <button
                      className="action-menu-button"
                      onClick={(e) => handleMenuClick(e, feature.id)}
                      title="Actions"
                    >
                      <i className="fas fa-ellipsis-v"></i> Actions
                    </button>
                    {openMenuId === feature.id && (
                      <div className="action-menu-dropdown">
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            onMove(feature.id, 'up');
                            setOpenMenuId(null);
                          }}
                          disabled={!canMoveUp}
                        >
                          <i className="fas fa-arrow-up"></i> Move Up
                        </button>
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            handleStartSort(feature.id);
                          }}
                        >
                          <i className="fas fa-arrows-alt"></i> Move
                        </button>
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            onMove(feature.id, 'down');
                            setOpenMenuId(null);
                          }}
                          disabled={!canMoveDown}
                        >
                          <i className="fas fa-arrow-down"></i> Move Down
                        </button>
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            onViewFeature && onViewFeature(feature);
                            setOpenMenuId(null);
                          }}
                        >
                          <i className="fas fa-eye"></i> View
                        </button>
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            onEdit(feature);
                            setOpenMenuId(null);
                          }}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            onAddSubFeature(feature);
                            setOpenMenuId(null);
                          }}
                        >
                          <i className="fas fa-plus"></i> Add Sub-feature
                        </button>
                        <button
                          className="action-menu-item"
                          onClick={() => {
                            setExportDialogFeature(feature);
                            setOpenMenuId(null);
                          }}
                        >
                          <i className="fas fa-download"></i> Export
                        </button>
                        <button
                          className="action-menu-item delete-action"
                          onClick={() => {
                            setOpenMenuId(null);
                            // Always show dialog for confirmation (replaces window.confirm)
                            setDeleteDialogFeature(feature);
                          }}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </td>
          )}
          {(mode === 'userVote' || mode === 'adminVote') && (
            <td className="votes-cell">
              {mode === 'userVote' && userCompany && (
                <div className="vote-controls">
                  {hasSubFeatures ? (
                    // Parent feature: show vote count pill
                    <div 
                      className={`parent-vote-pill ${childVoteTotal > 0 ? 'has-votes' : 'no-votes'}`}
                      title="You can't vote on folders, please choose a sub-feature"
                    >
                      <i className="fas fa-thumbs-up"></i>
                      <span className="parent-vote-count">{childVoteTotal}</span>
                    </div>
                  ) : (
                    // Leaf feature: show vote button
                    <>
                      <button
                        className={`vote-button vote-up ${companyVotes.get(feature.id) ? 'active' : ''} ${!canVote ? 'disabled' : ''}`}
                        onClick={() => {
                          if (canVote) {
                            handleVoteToggle(feature.id, companyVotes.get(feature.id) || false);
                          }
                        }}
                        disabled={!canVote}
                        title={canVote ? "Vote up" : `Cannot vote on features with status: ${status}`}
                      >
                        <i className="fas fa-thumbs-up"></i>
                      </button>
                      <span className="vote-count">{voteCounts.get(feature.id)?.up || 0}</span>
                    </>
                  )}
                </div>
              )}
              {mode === 'adminVote' && (
                <div 
                  className="admin-vote-display"
                  onMouseEnter={() => setHoveredVoteFeature(feature.id)}
                  onMouseLeave={() => setHoveredVoteFeature(null)}
                >
                  <span className="total-votes">{voteCounts.get(feature.id)?.total || 0}</span>
                  {hoveredVoteFeature === feature.id && voteCounts.get(feature.id)?.companyVotes && (
                    <div className="vote-tooltip">
                      {voteCounts.get(feature.id).companyVotes.map((cv, idx) => (
                        <div key={idx}>{cv.company_name}: {cv.voted ? 1 : 0}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </td>
          )}
        </tr>
        {hasSubFeatures && isExpanded && feature.subFeatures.map((subFeature, subIndex) => 
          renderFeatureRow(subFeature, level + 1, feature.subFeatures, subIndex)
        )}
      </React.Fragment>
    );
    /* Stable row callback: listing every handler as a dependency would retrigger this
       huge callback every render; handlers already close over current state. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expandedNodes,
    voteCounts,
    companyVotes,
    userCompany,
    mode,
    userRole,
    featureReleases,
    openMenuId,
    openStatusMenuId,
    openReleaseMenuId,
    openTaggingValueMenuId,
    hoveredVoteFeature,
    sortingFeatureId,
    filteredFeatures,
    releases,
    dimensionTagsMap,
    toggleRow,
    handleVoteToggle,
    handleStatusClick,
    handleStatusChange,
    handleReleaseClick,
    handleReleaseChange,
    handleTaggingValueClick,
    handleTaggingReleaseChange,
    handleTaggingTagChange,
    handleMenuClick,
    handleAfterThis,
    onViewFeature,
    onEdit,
    onDelete,
    onMove,
    onAddSubFeature,
    onReloadFeatures,
    getChildVoteTotal,
    getChildStatusCounts,
    getStatusClass,
    dimensions,
    featureTagsMap,
    taggingColumns,
    openParentTagSummaryId,
    getChildReleaseCounts,
    getChildTagCounts
  ]);

  // Calculate total votes cast by user's company
  const voteCount = userCompany && companyVotes.size > 0
    ? Array.from(companyVotes.values()).filter(v => v === true).length
    : 0;
  const totalCompanyVotes = userCompany?.votes_per_poll || 0;

  // Get all unique releases for filter options
  const allReleases = React.useMemo(() => {
    const releasesSet = new Set();
    featureReleases.forEach((releases) => {
      releases.forEach(release => {
        releasesSet.add(JSON.stringify({ id: release.id, name: release.name }));
      });
    });
    return Array.from(releasesSet).map(str => JSON.parse(str));
  }, [featureReleases]);

  // filterCount variable removed - was unused

  // Handle filter checkbox changes
  const handleStatusToggle = (status) => {
    const newSet = new Set(selectedStatuses);
    if (newSet.has(status)) {
      newSet.delete(status);
    } else {
      newSet.add(status);
    }
    setSelectedStatuses(newSet);
  };

  const handleReleaseToggle = (releaseId) => {
    const newSet = new Set(selectedReleases);
    if (newSet.has(releaseId)) {
      newSet.delete(releaseId);
    } else {
      newSet.add(releaseId);
    }
    setSelectedReleases(newSet);
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-dropdown-container')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  return (
    <div className="feature-table-container">
      <div className="filter-control">
        <input
          type="text"
          placeholder="Filter features by name or description..."
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          className="filter-input"
        />
        {mode === 'edit' && (
          <div className="filter-dropdown-container">
            <button
              className="filter-button"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              title="Filter by Status and Release"
            >
              <i className="fas fa-filter"></i> Filter
              {selectedStatuses.size > 0 && (
                <div className="filter-status-pills-inline">
                  {Array.from(selectedStatuses).map(status => (
                    <span key={status} className={`filter-status-pill-inline ${getStatusClass(status)}`}>
                      {status}
                    </span>
                  ))}
                </div>
              )}
              {selectedReleases.size > 0 && (
                <span className="filter-count-pill">{selectedReleases.size} release{selectedReleases.size > 1 ? 's' : ''}</span>
              )}
            </button>
            {showFilterDropdown && (
              <div className="filter-dropdown">
                <div className="filter-section">
                  <h4 className="filter-section-title">Status</h4>
                  <div className="filter-status-pills">
                    {STATUS_OPTIONS.map(status => {
                      const isSelected = selectedStatuses.has(status);
                      const statusClass = getStatusClass(status);
                      return (
                        <label key={status} className={`filter-status-pill-label ${isSelected ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleStatusToggle(status)}
                            className="filter-status-checkbox"
                          />
                          <span className={`filter-status-pill ${statusClass}`}>
                            {status}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="filter-section">
                  <h4 className="filter-section-title">Release</h4>
                  <div className="filter-checkboxes">
                    {allReleases.length === 0 ? (
                      <p className="filter-no-options">No releases available</p>
                    ) : (
                      allReleases.map(release => (
                        <label key={release.id} className="filter-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedReleases.has(release.id)}
                            onChange={() => handleReleaseToggle(release.id)}
                          />
                          <span>{release.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                {(selectedStatuses.size > 0 || selectedReleases.size > 0) && (
                  <div className="filter-actions">
                    <button
                      className="filter-clear-button"
                      onClick={() => {
                        setSelectedStatuses(new Set());
                        setSelectedReleases(new Set());
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="mode-selector">
          <span className="mode-icon">
            {mode === 'edit' && <i className="fas fa-list"></i>}
            {mode === 'tagging' && <i className="fas fa-tags"></i>}
            {mode === 'userVote' && <i className="fas fa-thumbs-up"></i>}
            {mode === 'adminVote' && <i className="fas fa-vote-yea"></i>}
          </span>
          <select
            value={mode}
            onChange={(e) => onModeChange && onModeChange(e.target.value)}
            className="mode-select"
            title="Select view mode"
          >
            <option value="edit">Status Mode</option>
            <option value="tagging">Tagging Mode</option>
            <option value="userVote">Vote Mode</option>
            {userRole === 'Owner' && (
              <option value="adminVote">Vote Mgmt Mode</option>
            )}
          </select>
        </div>
        {mode === 'tagging' && (
          <div className="tagging-view-selector-container">
            <button 
              className="tagging-view-selector-button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenViewMenu(!openViewMenu);
              }}
              title="Select tagging view"
            >
              {(() => {
                const currentView = views.find(v => v.id === currentViewId);
                return currentView ? `${currentView.name} View` : 'Default View';
              })()}
              <i className="fas fa-chevron-down"></i>
            </button>
            {openViewMenu && (
              <div className="tagging-view-menu-dropdown">
                {views.length === 0 ? (
                  <div className="tagging-view-menu-item" style={{ color: '#999', fontStyle: 'italic' }}>
                    No views configured
                  </div>
                ) : (
                  views.map(view => (
                    <button
                      key={view.id}
                      className={`tagging-view-menu-item ${currentViewId === view.id ? 'active' : ''}`}
                      onClick={() => {
                        setCurrentViewId(view.id);
                        setTaggingColumns(view.columns || [null, null, null]);
                        setOpenViewMenu(false);
                        try {
                          localStorage.setItem(TAGGING_CURRENT_VIEW_KEY, view.id);
                        } catch (err) {
                          logger.error('Error saving current view:', err);
                        }
                      }}
                    >
                      {view.name}
                      {currentViewId === view.id && (
                        <i className="fas fa-check"></i>
                      )}
                    </button>
                  ))
                )}
                <div className="tagging-view-menu-divider"></div>
                <button
                  className="tagging-view-menu-item"
                  onClick={() => {
                    setOpenViewMenu(false);
                    setShowViewsManagementDialog(true);
                  }}
                >
                  <i className="fas fa-cog"></i> Configure Views
                </button>
              </div>
            )}
          </div>
        )}
        {mode === 'edit' && userRole !== 'Guest' && (
          <button className="add-button-inline" onClick={() => onAddFeature()}>
            <i className="fas fa-plus"></i> Add Feature
          </button>
        )}
        {mode === 'userVote' && (
          <div className={`vote-summary ${votesLoading ? 'vote-summary-loading' : ''}`}>
            {votesLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading</span>
              </>
            ) : userCompany ? (
              <>Votes Cast: {voteCount} / {totalCompanyVotes}</>
            ) : (
              <span>Loading company...</span>
            )}
          </div>
        )}
      </div>

      {mode === 'userVote' && !userCompany && (
        <div className="no-company-warning">
          <i className="fas fa-exclamation-triangle"></i>
          <span>No Company to Vote</span>
        </div>
      )}

      <div className="table-wrapper">
        <table className="feature-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              {mode === 'edit' && <th>Status</th>}
              {mode === 'edit' && <th>Release</th>}
              {mode === 'edit' && userRole !== 'Guest' && <th>Actions</th>}
              {mode === 'tagging' && taggingColumns.map((col, index) => {
                if (!col) return <th key={index}></th>;
                const filterKey = `col${index}`;
                const columnFilter = taggingFilters.get(filterKey) || new Set();
                const hasFilter = columnFilter.size > 0;
                
                let headerContent;
                if (col === 'release') {
                  headerContent = 'Release';
                } else {
                  const dimension = dimensions.find(d => d.id === col);
                  headerContent = dimension?.name || 'Unknown';
                }
                
                return (
                  <th key={index}>
                    <div className="tagging-header-with-filter">
                      <span>{headerContent}</span>
                      <button
                        className={`tagging-header-filter-button ${hasFilter ? 'active' : ''}`}
                        onClick={() => {
                          setOpenFilterPanel({ colIndex: index, colValue: col });
                          setFilterPanelSearch('');
                        }}
                        title={hasFilter ? `Filter active (${columnFilter.size} selected)` : 'Filter'}
                      >
                        <i className="fas fa-filter"></i>
                        {hasFilter && (
                          <span className="filter-count-badge">{columnFilter.size}</span>
                        )}
                      </button>
                    </div>
                  </th>
                );
              })}
              {(mode === 'userVote' || mode === 'adminVote') && (
                <th>{mode === 'userVote' ? 'Vote' : 'Total Votes'}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredFeatures.length === 0 ? (
              <tr>
                <td colSpan={
                  mode === 'edit' ? (userRole === 'Guest' ? 4 : 5) :
                  mode === 'tagging' ? (2 + taggingColumns.filter(c => c).length) :
                  3
                } className="no-results">
                  No features found
                </td>
              </tr>
            ) : (
              filteredFeatures.map((feature, index) => renderFeatureRow(feature, 0, filteredFeatures, index))
            )}
          </tbody>
        </table>
      </div>

      {/* Tagging View Filter Panel */}
      {mode === 'tagging' && openFilterPanel && (
        <div className="tagging-filter-panel-overlay" onClick={() => setOpenFilterPanel(null)}>
          <div className="tagging-filter-panel" onClick={(e) => e.stopPropagation()}>
            <div className="tagging-filter-panel-header">
              <h3>
                {openFilterPanel.colValue === 'release' 
                  ? 'Filter Release' 
                  : (() => {
                      const dimension = dimensions.find(d => d.id === openFilterPanel.colValue);
                      return `Filter ${dimension?.name || 'Dimension'}`;
                    })()}
              </h3>
              <button
                className="tagging-filter-panel-close"
                onClick={() => setOpenFilterPanel(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {openFilterPanel.colValue !== 'release' && (() => {
              const dimension = dimensions.find(d => d.id === openFilterPanel.colValue);
              return dimension?.user_directions ? (
                <div className="tagging-filter-panel-directions">
                  <p>{dimension.user_directions}</p>
                </div>
              ) : null;
            })()}
            
            <div className="tagging-filter-panel-search">
              <input
                type="text"
                placeholder="Search..."
                value={filterPanelSearch}
                onChange={(e) => setFilterPanelSearch(e.target.value)}
                className="tagging-filter-search-input"
              />
            </div>
            
            <div className="tagging-filter-panel-content">
              {openFilterPanel.colValue === 'release' ? (
                <div className="tagging-filter-options">
                  {releases
                    .filter(release => 
                      !filterPanelSearch || 
                      release.name.toLowerCase().includes(filterPanelSearch.toLowerCase())
                    )
                    .map(release => {
                      const filterKey = `col${openFilterPanel.colIndex}`;
                      const columnFilter = taggingFilters.get(filterKey) || new Set();
                      const isChecked = columnFilter.has(release.id);
                      
                      return (
                        <label key={release.id} className="tagging-filter-option">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const filterKey = `col${openFilterPanel.colIndex}`;
                              setTaggingFilters(prev => {
                                const newMap = new Map(prev);
                                const currentFilter = newMap.get(filterKey) || new Set();
                                
                                if (e.target.checked) {
                                  currentFilter.add(release.id);
                                } else {
                                  currentFilter.delete(release.id);
                                }
                                
                                if (currentFilter.size > 0) {
                                  newMap.set(filterKey, currentFilter);
                                } else {
                                  newMap.delete(filterKey);
                                }
                                
                                return newMap;
                              });
                            }}
                          />
                          <span>{release.name}</span>
                        </label>
                      );
                    })}
                </div>
              ) : (
                <div className="tagging-filter-options">
                  {(() => {
                    const dimension = dimensions.find(d => d.id === openFilterPanel.colValue);
                    if (!dimension) return <div>Dimension not found</div>;
                    
                    const allTags = dimensionTagsMap.get(openFilterPanel.colValue) || [];
                    const sortedTags = [...allTags].sort((a, b) =>
                      (a.name || '').localeCompare(b.name || '')
                    );
                    
                    const filteredTags = sortedTags.filter(tag =>
                      !filterPanelSearch ||
                      tag.name.toLowerCase().includes(filterPanelSearch.toLowerCase())
                    );
                    
                    if (filteredTags.length === 0) {
                      return <div className="no-filter-options">No tags match your search</div>;
                    }
                    
                    return filteredTags.map(tag => {
                      const filterKey = `col${openFilterPanel.colIndex}`;
                      const columnFilter = taggingFilters.get(filterKey) || new Set();
                      const isChecked = columnFilter.has(tag.id);
                      
                      return (
                        <label key={tag.id} className="tagging-filter-option">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const filterKey = `col${openFilterPanel.colIndex}`;
                              setTaggingFilters(prev => {
                                const newMap = new Map(prev);
                                const currentFilter = newMap.get(filterKey) || new Set();
                                
                                if (e.target.checked) {
                                  currentFilter.add(tag.id);
                                } else {
                                  currentFilter.delete(tag.id);
                                }
                                
                                if (currentFilter.size > 0) {
                                  newMap.set(filterKey, currentFilter);
                                } else {
                                  newMap.delete(filterKey);
                                }
                                
                                return newMap;
                              });
                            }}
                          />
                          <span>{tag.name}</span>
                        </label>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            
            <div className="tagging-filter-panel-footer">
              <button
                className="tagging-filter-clear-all"
                onClick={() => {
                  const filterKey = `col${openFilterPanel.colIndex}`;
                  setTaggingFilters(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(filterKey);
                    return newMap;
                  });
                }}
              >
                Clear All
              </button>
              <button
                className="tagging-filter-close-button"
                onClick={() => setOpenFilterPanel(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Views Management Dialog */}
      {showViewsManagementDialog && (
        <div className="tagging-settings-dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowViewsManagementDialog(false);
            setEditingView(null);
            setViewName('');
          }
        }}>
          <div className="tagging-settings-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="tagging-settings-dialog-header">
              <h3>Configure Views</h3>
              <button
                className="tagging-settings-dialog-close"
                onClick={() => {
                  setShowViewsManagementDialog(false);
                  setEditingView(null);
                  setViewName('');
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="tagging-settings-dialog-content">
              {!editingView ? (
                // List of views
                <div className="views-list">
                  {views.length === 0 ? (
                    <div style={{ color: '#999', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                      No views configured. Click "Add View" to create one.
                    </div>
                  ) : (
                    views.map(view => (
                      <div key={view.id} className="view-list-item">
                        <div className="view-list-item-name">{view.name}</div>
                        <div className="view-list-item-actions">
                          <button
                            className="view-action-button"
                            onClick={() => {
                              setEditingView(view);
                              setViewName(view.name);
                            }}
                            title="Edit view"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="view-action-button"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete "${view.name}"?`)) {
                                const updatedViews = views.filter(v => v.id !== view.id);
                                setViews(updatedViews);
                                try {
                                  localStorage.setItem(TAGGING_VIEWS_KEY, JSON.stringify(updatedViews));
                                  // If deleted view was current, clear current view
                                  if (currentViewId === view.id) {
                                    setCurrentViewId(null);
                                    setTaggingColumns([null, null, null]);
                                    localStorage.removeItem(TAGGING_CURRENT_VIEW_KEY);
                                  }
                                } catch (err) {
                                  logger.error('Error saving views:', err);
                                }
                              }
                            }}
                            title="Delete view"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  <button
                    className="add-view-button"
                    onClick={() => {
                      setEditingView({ id: null, name: '', columns: [null, null, null] });
                      setViewName('');
                    }}
                  >
                    <i className="fas fa-plus"></i> Add View
                  </button>
                </div>
              ) : (
                // Add/Edit view form
                <div className="view-edit-form">
                  <div className="view-edit-field">
                    <label>View Name</label>
                    <input
                      type="text"
                      value={viewName}
                      onChange={(e) => setViewName(e.target.value)}
                      placeholder="Enter view name"
                      className="view-name-input"
                    />
                  </div>
                  <div className="view-edit-columns">
                    <label>Columns</label>
                    {[0, 1, 2].map(colIndex => {
                      const col = editingView.columns[colIndex];
                      return (
                        <div key={colIndex} className="tagging-settings-column-selector">
                          <label>Column {colIndex + 1}</label>
                          <select
                            value={col || ''}
                            onChange={(e) => {
                              const newColumns = [...editingView.columns];
                              const value = e.target.value || null;
                              
                              // Prevent selecting the same value in multiple columns
                              if (value && editingView.columns.includes(value)) {
                                const existingIndex = editingView.columns.indexOf(value);
                                newColumns[existingIndex] = null;
                              }
                              
                              newColumns[colIndex] = value;
                              setEditingView({ ...editingView, columns: newColumns });
                            }}
                            className="tagging-settings-column-select"
                          >
                            <option value="">-- None --</option>
                            <option value="release">Release</option>
                            {dimensions.map(dim => (
                              <option key={dim.id} value={dim.id}>
                                {dim.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  <div className="view-edit-actions">
                    <button
                      className="view-save-button"
                      onClick={() => {
                        if (!viewName.trim()) {
                          alert('Please enter a view name');
                          return;
                        }
                        
                        let updatedViews;
                        if (editingView.id) {
                          // Update existing view
                          updatedViews = views.map(v => 
                            v.id === editingView.id 
                              ? { ...editingView, name: viewName.trim() }
                              : v
                          );
                        } else {
                          // Add new view
                          const newView = {
                            id: `view-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            name: viewName.trim(),
                            columns: editingView.columns
                          };
                          updatedViews = [...views, newView];
                        }
                        
                        setViews(updatedViews);
                        try {
                          localStorage.setItem(TAGGING_VIEWS_KEY, JSON.stringify(updatedViews));
                          if (!editingView.id) {
                            // If it's a new view, select it
                            const newView = updatedViews[updatedViews.length - 1];
                            setCurrentViewId(newView.id);
                            setTaggingColumns(newView.columns);
                            localStorage.setItem(TAGGING_CURRENT_VIEW_KEY, newView.id);
                          } else if (currentViewId === editingView.id) {
                            // If editing current view, update columns
                            setTaggingColumns(editingView.columns);
                          }
                        } catch (err) {
                          logger.error('Error saving views:', err);
                        }
                        
                        setEditingView(null);
                        setViewName('');
                      }}
                    >
                      {editingView.id ? 'Save' : 'Add'}
                    </button>
                    <button
                      className="view-cancel-button"
                      onClick={() => {
                        setEditingView(null);
                        setViewName('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ExportFeatureDialog
        isOpen={exportDialogFeature !== null}
        feature={exportDialogFeature}
        onClose={() => setExportDialogFeature(null)}
        onExport={() => {
          // Optionally refresh features after export
        }}
      />

      {/* Delete Confirmation Dialog */}
      {deleteDialogFeature && (
        <div className="delete-dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !deleteInProgress) {
            setDeleteDialogFeature(null);
          }
        }}>
          <div className="delete-dialog">
            <div className="delete-dialog-header">
              <h3>Delete Feature</h3>
              <button
                className="delete-dialog-close"
                onClick={() => {
                  if (!deleteInProgress) {
                    setDeleteDialogFeature(null);
                  }
                }}
                disabled={deleteInProgress}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="delete-dialog-content">
              <p>
                Are you sure you want to delete <strong>{deleteDialogFeature.name}</strong>?
              </p>
              {(() => {
                const childCount = countAllChildren(deleteDialogFeature);
                if (childCount > 0) {
                  return (
                    <>
                      <p className="delete-dialog-warning">
                        This will also delete <strong>{childCount}</strong> child feature{childCount !== 1 ? 's' : ''}.
                      </p>
                      {deleteInProgress && (
                        <div className="delete-dialog-progress">
                          <p className="delete-dialog-progress-text">
                            Deleting {deleteProgress.current} of {deleteProgress.total} feature{deleteProgress.total !== 1 ? 's' : ''}...
                          </p>
                          <div className="delete-dialog-progress-bar">
                            <div 
                              className="delete-dialog-progress-bar-fill"
                              style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                }
                return (
                  <p className="delete-dialog-info">
                    This action cannot be undone.
                  </p>
                );
              })()}
            </div>
            <div className="delete-dialog-footer">
              <button
                className="delete-dialog-button delete-dialog-button-cancel"
                onClick={() => {
                  if (!deleteInProgress) {
                    setDeleteDialogFeature(null);
                  }
                }}
                disabled={deleteInProgress}
              >
                Cancel
              </button>
              <button
                className="delete-dialog-button delete-dialog-button-delete"
                onClick={async () => {
                  setDeleteInProgress(true);
                  try {
                    const childCount = countAllChildren(deleteDialogFeature);
                    
                    if (childCount > 0) {
                      // Collect all IDs to delete (children first, then parent)
                      const childIds = collectAllChildIds(deleteDialogFeature);
                      const allIds = [...childIds, deleteDialogFeature.id];
                      
                      // Set total for progress tracking
                      setDeleteProgress({ current: 0, total: allIds.length });
                      
                      // Delete all features one by one, showing progress
                      for (let i = 0; i < allIds.length; i++) {
                        await deleteFeature(allIds[i]);
                        setDeleteProgress({ current: i + 1, total: allIds.length });
                      }
                    } else {
                      // No children, just delete the single feature
                      setDeleteProgress({ current: 0, total: 1 });
                      await deleteFeature(deleteDialogFeature.id);
                      setDeleteProgress({ current: 1, total: 1 });
                    }
                    
                    // Reload features once after all deletions complete
                    if (onReloadFeatures) {
                      await onReloadFeatures();
                    } else {
                      // Fallback: trigger reload via onDelete (will fail but triggers reload)
                      try {
                        await onDelete(deleteDialogFeature.id);
                      } catch (reloadErr) {
                        // Ignore error - feature is already deleted, we just want the reload
                        logger.debug('Reload after deletion:', reloadErr);
                      }
                    }
                    
                    // Use a small delay to show completion before closing
                    await new Promise(resolve => setTimeout(resolve, 300));
                    
                    // Close dialog after all deletions complete
                    setDeleteDialogFeature(null);
                    setDeleteProgress({ current: 0, total: 0 });
                  } catch (err) {
                    logger.error('Error deleting features:', err);
                    // Keep dialog open on error so user can see what happened
                    setDeleteProgress({ current: 0, total: 0 });
                  } finally {
                    setDeleteInProgress(false);
                  }
                }}
                disabled={deleteInProgress}
              >
                {deleteInProgress ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i> Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize FeatureTable component to prevent unnecessary re-renders when props haven't changed
export default React.memo(FeatureTable);
